import React, { useState, useEffect, useCallback, useMemo } from 'react';

import moment from 'moment';
import { AxiosError } from 'axios';
import { Button, Alert, Modal, Glyphicon } from 'react-bootstrap';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import Spinner from '../components/utils/Spinner';
import TopPanel from '../components/utils/TopPanel';
import FieldView from '../components/utils/FieldView';
import { getMatchByPath } from '../infrastructure/utils/common';
import * as Service from '../services/VendorsService';
import * as ActivitiesService from '../services/ActivitiesService';
import * as PartiesService from '../services/PartiesService';
import { CDT } from '../infrastructure/constants/common';
import VendorActivitiesList from '../components/vendors/VendorActivitiesList';
import { BookingPlatfromActivities, Vendor } from '../types/Vendor';
import VendorConnectedStripeAccountsList from '../components/vendors/VendorConnectedStripeAccountsList';
import BookingPlatformSelector from '../components/vendors/BookingPlatformSelector';
import {
  Activity,
  ActivitySource,
  ActivitySourceValue,
  BookingOption,
} from '../types/Activity';
import VendorBookingPlatformActivitiesList from '../components/vendors/VendorBookingPlatformActivitiesList';
import { getActivityFromBookingPlatform } from '../services/VendorsService';
import ActivityCreateModal from '../components/activities/ActivityModals/ActivityCreateModal/ActivityCreateModal';
import BookingOptionsAppendForm from '../components/fareharborItems/BookingOptionsAppendForm';
import { PartyType } from '../types/Party';
import { getVendorAssociatedUsers, signupVendor } from '../api/VendorsApi';
import { useFetchData } from '../infrastructure/hooks/useFetchData';
import { ApiStatus } from '../infrastructure/constants/apiStatus';
import { User } from '../api/UsersAPI';
import { BillingInfo } from '../schemas/VendorSchema';
import ParentVendorSelector from '../components/vendors/ParentVendorSelector/ParentVendorSelector';
import VendorTierSelector from '../components/vendors/VendorTierSelector/VendorTierSelector';
import Switcher from '../components/utils/FormWidgets/Switcher';
import VendorDeactivationWarningModal from '../components/vendors/VendorDeactivationWarningModal';
import { getConfig } from '../config';
import StyledFieldset from '../components/utils/Styles/StyledFieldset';
/* eslint-disable no-shadow */

type BuildBookingOptions = {
  variants: BookingOption[];
  sourceId: string;
  source: ActivitySource;
};

const IsActiveSwitch = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: end;
  font-size: 18px;
  gap: 10px;
`;

const vendorStatusCaption = (status: string) =>
  status === 'active' ? 'Active' : 'Inactive';
const isVendorActive = (status: string) => status === 'active';

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/prop-types
const VendorContainer = ({ history }) => {
  const [loading, setLoading] = useState(true);
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [activities, setActivities] = useState<Activity[] | null>(null);
  const [bookingPlatformActivities, setBookingPlatformActivities] = useState<
    BookingPlatfromActivities[] | null
  >(null);
  const [partyTypes, setPartyTypes] = useState<PartyType[]>([]);

  const [builtActivity, setBuiltActivity] = useState<Partial<Activity> | null>(
    null
  );
  const [
    builtBookingOptions,
    setBuiltBookingOptions,
  ] = useState<BuildBookingOptions | null>(null);

  const [allTags, setAllTags] = useState<string[]>([]);

  const [
    apiStatus,
    associatedUsers = [],
    fetchAssociatedUsers,
    refetchAssociatedUsers,
  ] = useFetchData<User['Item'][]>();

  const fetchData = async id => {
    try {
      setLoading(true);
      const [
        retrievedVendor,
        activities,
        bookingPlatformActivities,
        tags,
        partyTypes,
      ] = (
        await Promise.allSettled([
          Service.fetchVendorById(id),
          ActivitiesService.searchByVendor(id),
          Service.getBookingPlatformActivities(id),
          ActivitiesService.getAllTags(),
          PartiesService.listActivePartyTypes(),
        ])
      ).map(result => result.status === 'fulfilled' && result.value);

      setBookingPlatformActivities(bookingPlatformActivities);
      setVendor(retrievedVendor);
      setActivities(activities);

      setAllTags(tags);
      setPartyTypes(partyTypes);
    } catch (e) {
      console.error(e);
      const error = e as AxiosError;
      alert(
        `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`
      );
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/prop-types
    history.goBack();
  };

  const updateVendor = useCallback(
    async updatedVendor => {
      try {
        setLoading(true);
        const result = await Service.updateBookingVendor({
          id: vendor?.id,
          ...updatedVendor,
        });
        setVendor(result);
        fetchData(vendor?.id);
      } catch (e) {
        console.error(e);
        const error = e as AxiosError;
        alert(
          `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`
        );
      } finally {
        setLoading(false);
      }
    },
    [vendor?.id]
  );

  const { stripeConnectedAccounts } = vendor ?? { stripeConnectedAccounts: [] };

  const updateVendorBillingInfo = useCallback(
    async (billingInfo: BillingInfo, assignedTier: string | undefined) => {
      await updateVendor({
        ...vendor,
        assignedTier,
        billingInfo,
      });
    },
    [updateVendor, vendor]
  );

  const onExperienceOrVariantCreateClick = async (
    itemId: string,
    isExperience: boolean = true
  ) => {
    const { id, type } = vendor ?? { id: null, type: null };
    if (!id && !type) {
      return;
    }
    setLoading(true);
    try {
      const activity = await getActivityFromBookingPlatform(id, itemId, type);
      if (activity) {
        const { bookingOptions, sourceId, source } = activity;
        if (isExperience) {
          setBuiltActivity(activity);
        } else if (bookingOptions && bookingOptions.length > 0) {
          setBuiltBookingOptions({
            variants: bookingOptions,
            sourceId,
            source,
          });
        }
      }
    } catch (e) {
      const error = e as AxiosError;
      alert(
        `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`
      );
    } finally {
      setLoading(false);
    }
  };

  const onActivitySave = async (
    activity: Activity,
    bookingOptions: BookingOption[],
    selectedActivity: Activity
  ) => {
    const { id: vendorId } = vendor ?? { id: undefined };
    try {
      setLoading(true);
      const { id, ...restOfActivity } = activity;

      // Make sure that all booking options have a subType
      const updatedBookingOptions = bookingOptions.map(option => ({
        ...option,
        subType: option.subType ?? 'bookingOption',
      }));

      const {
        id: selectedActivityId,
        cancellationPolicy,
        description,
        pictures,
        popularity,
        supplierId,
        tags = [],
        vendorId: selectedActivityVendorId,
        vendorName,
      } = selectedActivity ?? {};

      if (selectedActivity) {
        await ActivitiesService.updateActivity(selectedActivityId, {
          ...restOfActivity,
          bookingOptions: updatedBookingOptions,
          cancellationPolicy,
          description,
          pictures,
          popularity,
          supplierId,
          tags,
          vendorId: selectedActivityVendorId,
          vendorName,
        });
      } else {
        await ActivitiesService.createActivity({
          ...activity,
          bookingOptions: updatedBookingOptions,
        });
      }
      setBuiltActivity(null);
      if (vendorId) {
        await fetchData(vendorId);
      }
    } catch (e: unknown) {
      const error = e as AxiosError;
      console.error(error);
      alert(
        `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`
      );
    } finally {
      setLoading(false);
    }
  };

  const onVariantSave = async activityId => {
    if (!builtBookingOptions) {
      return;
    }
    try {
      setLoading(true);
      const activity = await ActivitiesService.getById(activityId);
      const { id, ...restOfActivity } = activity;
      const updated = {
        ...restOfActivity,
        bookingOptions: [
          ...(restOfActivity.bookingOptions || []),
          ...(builtBookingOptions.variants?.map(variant => ({
            ...variant,
            subType: variant.subType ?? 'bookingOption',
          })) || []),
        ],
        category: restOfActivity.category || '',
        type: restOfActivity.type || '',
        sourceId: builtBookingOptions.sourceId,
        source: builtBookingOptions.source,
      };
      await ActivitiesService.updateActivity(activityId, updated);
      setBuiltBookingOptions(null);
    } catch (e) {
      const error = e as AxiosError;
      alert(
        `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`
      );
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const match = getMatchByPath(
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/prop-types
      history.location.pathname,
      '/vendors/:type/:id'
    );
    const { id } = match.params;
    fetchData(id);
  }, []);

  useEffect(() => {
    const id = vendor?.id;
    if (!id) return;

    fetchAssociatedUsers(() => getVendorAssociatedUsers(id));
  }, [fetchAssociatedUsers, vendor?.id]);

  const [showDeactivationWarning, setShowDeactivationWarning] = useState(false);
  const handleVendorStatusChanged = async (active: boolean) => {
    if (!active) {
      setShowDeactivationWarning(true);
    } else {
      await updateVendor({
        ...vendor,
        status: 'active',
      });
    }
  };
  const handleDeactivationConfirmed = async () => {
    await Promise.all([
      (activities || []).map(a =>
        ActivitiesService.updateActivity(a.id, {
          ...a,
          status: 'unpublished',
          tags: Array.from(new Set([...(a.tags || []), 'hidden'])),
        })
      ),
    ]);

    await updateVendor({
      ...vendor,
      status: 'inactive',
      deactivationDate: new Date().toISOString(),
    });

    setShowDeactivationWarning(false);
  };

  const handleParentVendorSave = async (id: string | undefined) => {
    let updates = {
      ...vendor,
      parentVendorId: id ?? null,
    };

    //remove subscription tier
    if (vendor?.billingInfo) {
      updates = {
        ...updates,
        billingInfo: {
          userId: vendor.billingInfo.userId,
        },
      };
    }

    await updateVendor(updates);
  };

  const config = getConfig();

  return (
    <>
      {showDeactivationWarning && (
        <VendorDeactivationWarningModal
          onConfirm={handleDeactivationConfirmed}
          onCancel={() => setShowDeactivationWarning(false)}
        />
      )}
      {builtActivity && (
        <ActivityCreateModal
          isOpen={!!builtActivity}
          closeModal={() => setBuiltActivity(null)}
          allTags={allTags}
          // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
          // @ts-ignore
          createActivity={onActivitySave}
          data={builtActivity}
          existingSource={vendor?.type as ActivitySourceValue}
          showActivitySelector
          partyTypes={partyTypes}
        />
      )}
      {builtBookingOptions && (
        <BookingOptionsAppendForm
          closeModal={() => setBuiltBookingOptions(null)}
          onSave={onVariantSave}
        />
      )}
      {loading && <Spinner />}
      {!loading && vendor && (
        <>
          <TopPanel>
            <div
              role='button'
              tabIndex={0}
              // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
              // @ts-ignore
              type='button'
              className='btn-nav back'
              onClick={() => {
                goBack();
              }}
              style={{ marginLeft: '15px' }}
              aria-label='left Align'
              aria-hidden='true'
            >
              <i
                className='fas fa-arrow-left'
                style={{ paddingRight: '8px' }}
              />
              Back
            </div>
            <div className='page-actions'>
              <a
                href={`${config.batchAdminURL}/partners/${vendor.id}/partner-details`}
                className='btn'
                target='_blank'
                rel='noreferrer'
              >
                Open in Admin
              </a>
            </div>
          </TopPanel>
          <div className='inner-page-container'>
            <div
              className='col-md-12 col-sm-12 col-xs-12'
              style={{ padding: '0px' }}
            >
              <StyledFieldset disabled>
                <div className='data-box'>
                  <h1
                    className='col-md-6 col-sm-6 col-xs-6'
                    style={{ paddingLeft: '0px', marginTop: '0px' }}
                  >
                    {vendor.name}
                  </h1>
                  <IsActiveSwitch className='col-md-6 col-sm-6 col-xs-6'>
                    <span
                      style={{
                        color: isVendorActive(vendor.status)
                          ? 'darkgreen'
                          : 'grey',
                      }}
                    >
                      {vendorStatusCaption(vendor.status)}
                    </span>
                    <Switcher
                      style={{ display: 'inline-block', marginTop: '7px' }}
                      checked={isVendorActive(vendor.status)}
                      onChange={handleVendorStatusChanged}
                      disabled={false}
                      id='isVendorActive'
                    />
                  </IsActiveSwitch>
                  <div className='clearfix' />
                  <FieldView name='Public Email*'>
                    {vendor.publicEmail}
                  </FieldView>
                  <FieldView name='Public Phone Number*'>
                    {vendor.publicPhoneNumber}
                  </FieldView>
                  <BizdevContactSection contacts={vendor.contacts} />
                  <FieldView name='Vendor Portal Users'>
                    <InviteVendorForm
                      vendors={[vendor.id]}
                      refetchAssociatedUsers={refetchAssociatedUsers}
                      disabled={apiStatus === ApiStatus.loading}
                    />
                  </FieldView>
                  <FieldView name='Parent Vendor'>
                    <ParentVendorSelector
                      onSave={handleParentVendorSave}
                      initialId={vendor.parentVendorId}
                    />
                  </FieldView>
                  <FieldView name='Vendor Tier'>
                    <VendorTierSection
                      disabled={!!vendor.parentVendorId}
                      billingInfo={vendor.billingInfo}
                      updateVendorBillingInfo={updateVendorBillingInfo}
                    />
                  </FieldView>
                  <AssociatedUsersView
                    isLoading={apiStatus !== ApiStatus.success}
                    associatedUsers={associatedUsers}
                  />
                  <FieldView name='Created At'>
                    {moment(vendor.createdAt)
                      .tz(CDT)
                      .format('MM/DD/YY hh:mm A ')}
                  </FieldView>
                  <FieldView name='Updated At'>
                    {moment(vendor.updatedAt)
                      .tz(CDT)
                      .format('MM/DD/YY hh:mm A ')}
                  </FieldView>
                </div>
              </StyledFieldset>
              {activities && (
                <div className='data-box'>
                  <h4 className='header'>Activities:</h4>
                  <VendorActivitiesList activities={activities} />
                </div>
              )}
              <div className='data-box'>
                <BookingPlatformSelector
                  type={vendor.type}
                  bookingPlatforms={vendor.bookingPlatforms}
                  onSave={updateVendor}
                />
              </div>
              <StyledFieldset disabled>
                <div className='data-box'>
                  {stripeConnectedAccounts?.length > 0 && (
                    <VendorConnectedStripeAccountsList
                      stripeConnectedAccounts={stripeConnectedAccounts}
                    />
                  )}
                </div>
              </StyledFieldset>
              {bookingPlatformActivities &&
                bookingPlatformActivities.length > 0 && (
                  <div className='data-box'>
                    <h4 className='header'>Booking Platform Activities:</h4>
                    <VendorBookingPlatformActivitiesList
                      activities={bookingPlatformActivities}
                      onExperienceOrVariantCreateClick={
                        onExperienceOrVariantCreateClick
                      }
                    />
                  </div>
                )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

type VendorContactSectionProps = Pick<Vendor, 'contacts'>;

function BizdevContactSection({ contacts }: VendorContactSectionProps) {
  const { bizdev } = contacts ?? {};
  const { email = '', name = '', phone = '' } = bizdev ?? {};

  const items = useMemo(() => {
    return [
      { name: 'Name', value: name, cond: !!name },
      { name: 'Email', value: email, cond: !!email },
      { name: 'Phone Number', value: phone, cond: !!phone },
    ].filter(({ cond }) => cond);
  }, [email, name, phone]);

  if (items.length === 0) return null;

  return (
    <div className='data-box'>
      <h4 className='header'>Bizdev:</h4>
      {items.map(({ name, value }) => (
        <FieldView key={name} name={name}>
          {value}
        </FieldView>
      ))}
    </div>
  );
}

const FormDataSchema = z.object({
  name: z
    .string({ required_error: 'Name is required' })
    .min(1, { message: 'Name is required' }),
  phone: z
    .string()
    .refine(
      (value: string) => !value || value.replace(/\D/g, '').length >= 10,
      { message: 'Please enter a valid phone number' }
    ),
  email: z.string({ required_error: 'Email is required' }).email().trim(),
  vendors: z.array(z.string()).default([]),
});

type FormData = z.infer<typeof FormDataSchema>;

type EmailFormProps = {
  vendors: string[];
  refetchAssociatedUsers(): void;
  disabled: boolean;
};

type AssociatedUsersViewProps = {
  isLoading: boolean;
  associatedUsers: User['Item'][];
};

const Pill = styled.div`
  border-radius: 2px;
  color: hsl(0, 0%, 20%);
  font-size: 85%;
  overflow: hidden;
  text-align: center;
  padding: 3px;
  text-overflow: ellipsis;
  white-space: nowrap;
  box-sizing: border-box;
  background-color: #d8e6f1 !important;
`;

const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2px;
`;

function AssociatedUsersView({
  isLoading,
  associatedUsers = [],
}: AssociatedUsersViewProps) {
  if (!isLoading && associatedUsers.length === 0) return null;
  return (
    <FieldView name={'Associated Users'}>
      <FlexRow>
        {associatedUsers.map(user => (
          <Pill key={user}>{user.email}</Pill>
        ))}
      </FlexRow>
    </FieldView>
  );
}

const VendorFormContainer = styled.form`
  .modal-body {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: center;
  justify-content: center;
  label {
    flex: 1;
  }
  input {
    flex: 2;
  }
  span {
    padding: 24px 0px;
  }
`;

function InviteVendorForm({
  vendors,
  refetchAssociatedUsers,
  disabled,
}: EmailFormProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const toogle = () => setIsModalOpen(!isModalOpen);

  return (
    <>
      <InviteVendorFormModal
        show={isModalOpen}
        onHide={toogle}
        vendors={vendors}
        refetchAssociatedUsers={refetchAssociatedUsers}
      />
      <Button
        onClick={toogle}
        className='submit'
        bsStyle='primary'
        disabled={disabled}
      >
        Invite
      </Button>
    </>
  );
}

type EmailFormModalProps = {
  show: boolean;
  onHide(): void;
} & Pick<EmailFormProps, 'vendors' | 'refetchAssociatedUsers'>;

function InviteVendorFormModal({
  show,
  onHide,
  vendors,
  refetchAssociatedUsers,
}: EmailFormModalProps) {
  const { handleSubmit, formState, register, reset } = useForm<FormData>({
    defaultValues: {
      vendors,
    },
    resolver: zodResolver(FormDataSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const [isLoading, setIsLoading] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string>('');

  const hideMessage = () => {
    setTimeout(() => {
      setErrorMessage('');
    }, 5000);
  };

  const onSubmit = handleSubmit(async data => {
    try {
      setIsLoading(true);
      await signupVendor(data);
      reset();
      refetchAssociatedUsers();
      onHide();
    } catch (e) {
      const { message } =
        e instanceof Error ? e : new Error('Unable to signup vendor');
      setErrorMessage(message);
      hideMessage();
    } finally {
      setIsLoading(false);
    }
  });

  return (
    <Modal
      show={show}
      onHide={() => {
        reset();
        onHide();
      }}
    >
      {isLoading ? (
        <Row>
          <Glyphicon glyph='refresh' className='spinning' />
        </Row>
      ) : (
        <VendorFormContainer onSubmit={onSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Vendor Portal Users</Modal.Title>
            {errorMessage.length > 0 && (
              <Alert bsStyle='danger'>{errorMessage}</Alert>
            )}
          </Modal.Header>
          <Modal.Body>
            <Row>
              <label>Name</label>
              <input
                type='name'
                className='form-control'
                {...register('name', { required: true })}
              />
            </Row>
            {formState.errors.name?.message && (
              <Alert bsStyle='danger'>{formState.errors.name.message}</Alert>
            )}
            <Row>
              <label>Phone</label>
              <input
                type='phone'
                className='form-control'
                {...register('phone', { required: true })}
              />
            </Row>
            {formState.errors.phone?.message && (
              <Alert bsStyle='danger'>{formState.errors.phone.message}</Alert>
            )}
            <Row>
              <label>Email</label>
              <input
                type='email'
                className='form-control'
                {...register('email', { required: true })}
              />
            </Row>
            {formState.errors.email?.message && (
              <Alert bsStyle='danger'>{formState.errors.email.message}</Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button
              className='btn btn-def'
              onClick={() => {
                reset();
                onHide();
              }}
            >
              Cancel
            </button>
            <Button type='submit' className='submit' bsStyle='primary'>
              Invite
            </Button>
          </Modal.Footer>
        </VendorFormContainer>
      )}
    </Modal>
  );
}

const VendorFeeFormDataSchema = z.object({
  assignedTier: z.string().optional(),
  pricingTableId: z.string().nullish(),
});

type VendorFeeFormData = z.infer<typeof VendorFeeFormDataSchema>;

type VendorFeeSectionProps = Pick<Vendor, 'assignedTier' | 'billingInfo'> & {
  updateVendorBillingInfo(
    billingInfo: BillingInfo,
    assignedTier: string | undefined
  ): void;
  disabled?: boolean;
};

const StyledForm = styled.form`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 24px;
  .select {
    width: 300px;
  }
`;

function VendorTierSection({
  assignedTier,
  billingInfo,
  updateVendorBillingInfo,
  disabled,
}: VendorFeeSectionProps) {
  const { pricingTableId, userId } = billingInfo ?? {};

  const { handleSubmit, setValue } = useForm<VendorFeeFormData>({
    resolver: zodResolver(VendorFeeFormDataSchema),
    defaultValues: {
      assignedTier,
      pricingTableId,
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const onSubmit = handleSubmit(data => {
    updateVendorBillingInfo(
      {
        pricingTableId: data.pricingTableId,
        userId,
      },
      data.assignedTier
    );
  });

  return (
    <StyledForm onSubmit={onSubmit}>
      <VendorTierSelector
        onSelect={({ pricingTableId, name }) => {
          setValue('pricingTableId', pricingTableId);
          setValue('assignedTier', name);
        }}
        initialValue={pricingTableId}
        disabled={disabled}
      />
      <Button type='submit' className='submit' bsStyle='primary'>
        Save
      </Button>
    </StyledForm>
  );
}

export default VendorContainer;
